import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { EXCLUSIVITY_SCOPES } from "../brandDealUtils";
import { Badge, BmModal, Btn, Checkbox, Field, Select, TextArea, Toggle } from "../ui/BrandUI";

const MAX_BYTES = 3 * 1024 * 1024;

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// "TERMS & EXCLUSIVITY" modal, in standard (Cache contract) or custom
// (brand-uploaded PDF) mode. Confirms with the settings the deal stores.
const ContractTermsModal = ({ mode = "standard", initial, athleteName, onConfirm, onClose, busy = false }) => {
  const fileRef = useRef(null);
  const [enabled, setEnabled] = useState(initial?.exclusivity?.enabled ?? true);
  const [days, setDays] = useState(initial?.exclusivity?.durationDays ?? 14);
  const [scope, setScope] = useState(initial?.exclusivity?.scope || "same_category");
  const [scopeOfWork, setScopeOfWork] = useState(initial?.scopeOfWork || "");
  const [requireProof, setRequireProof] = useState(initial?.requireProof ?? true);
  const [autoEmail, setAutoEmail] = useState(initial?.autoEmail ?? true);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(initial?.fileName || "");

  const pickFile = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (picked.size > MAX_BYTES) {
      toast.error("Contract files must be under 3 MB.");
      return;
    }
    setFile(picked);
    setFileName(picked.name);
  };

  const confirm = async () => {
    let upload = null;
    if (mode === "custom") {
      if (!file && !fileName) {
        toast.error("Upload your contract first.");
        return;
      }
      if (file) upload = { fileName: file.name, dataUrl: await readAsDataUrl(file) };
    }
    onConfirm(
      {
        type: mode,
        fileName,
        exclusivity: { enabled, durationDays: Number(days) || 0, scope },
        scopeOfWork,
        requireProof,
        autoEmail,
      },
      upload,
    );
  };

  return (
    <BmModal title={athleteName ? `Contract terms · ${athleteName}` : "Contract terms"} onClose={onClose} width={960}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="bm-h-m text-bm-ink">TERMS &amp; EXCLUSIVITY</h3>
          {mode === "standard" && <Badge tone="passive">Cache Standard Contract</Badge>}
        </div>

        {mode === "custom" && (
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={pickFile} />
            <Btn variant="secondary" onClick={() => fileRef.current?.click()}>
              Upload contract
            </Btn>
            <span className="bm-body-m text-bm-ink">{fileName || "No file selected"}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="bm-label-l text-bm-ink">Restrict athletes from promoting competing brands</span>
            <span className="bm-body-s text-bm-muted">Enable exclusivity constraints during active campaign dates</span>
          </div>
          <Toggle on={enabled} onChange={setEnabled} label="Exclusivity" />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Duration">
            <span className="flex h-10 items-center gap-2 rounded-[8px] border border-bm-border px-3 focus-within:border-bm-orange">
              <input
                type="number"
                min={0}
                max={365}
                value={days}
                disabled={!enabled}
                onChange={(e) => setDays(e.target.value)}
                className="bm-bare w-16 bm-body-m text-bm-ink disabled:text-bm-tertiary"
              />
              <span className="bm-body-m text-bm-ink">days</span>
            </span>
          </Field>
          <Field label="Exclusivity">
            <Select value={scope} disabled={!enabled} onChange={(e) => setScope(e.target.value)}>
              {EXCLUSIVITY_SCOPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Scope of work">
          <TextArea
            value={scopeOfWork}
            onChange={(e) => setScopeOfWork(e.target.value)}
            className="!min-h-[244px] !bm-body-s !text-bm-muted"
            placeholder="Paste or edit the scope of work the athlete will sign."
          />
        </Field>

        <Checkbox checked={requireProof} onChange={setRequireProof} label="Require proof of content (screenshot + link) before releasing payments" />
        <Checkbox checked={autoEmail} onChange={setAutoEmail} label="Auto-send email notification when offer is sent" />

        <div className="mt-2 flex gap-4">
          <Btn className="flex-1" onClick={confirm} disabled={busy}>
            {busy ? "Sending..." : "Confirm"}
          </Btn>
          <Btn variant="secondary" className="flex-1" onClick={onClose}>
            Cancel
          </Btn>
        </div>
      </div>
    </BmModal>
  );
};

export default ContractTermsModal;
